"use client";
import React, { useRef } from "react";
import { motion, useInView } from "motion/react";
import { useTranslations } from "next-intl";
import AnimatedCard from "@/components/ui/animated-card";
import BorderWrapper from "@/components/ui/border-wrapper";
import PageCta from "@/components/shared/page-cta";

const shapeVariants = {
  visible: {
    scale: [0.9, 1, 0.9] as number[],
    transition: { duration: 5, ease: "easeInOut", repeat: Infinity },
  },
} as const;

const containerVariants = {
  hidden: {},
  show: { transition: { staggerChildren: 0.09 } },
} as const;

const itemVariants = {
  hidden: { opacity: 0, y: 24 },
  show: { opacity: 1, y: 0, transition: { duration: 0.5, ease: "easeOut" } },
} as const;

const IntegrationsWhy = () => {
  const t = useTranslations("integrations");

  const whyItems = [
    { title: t("why.items.0.title"), description: t("why.items.0.description") },
    { title: t("why.items.1.title"), description: t("why.items.1.description") },
    { title: t("why.items.2.title"), description: t("why.items.2.description") },
    { title: t("why.items.3.title"), description: t("why.items.3.description") },
  ];
  const whyRef = useRef<HTMLElement>(null);
  const whyInView = useInView(whyRef, { once: true, margin: "0px 0px -80px 0px" });

  return (
    <>
      {/* ── WHY FLYINGCODE ── */}
      <section ref={whyRef} className="section pb-[60px] lg:pb-[140px] relative">
        <div
          className="absolute right-[-5%] top-[20%] w-[500px] h-[500px] bg-[linear-gradient(180deg,_rgba(150,128,197,0.1)_0%,_rgba(34,12,81,0)_100%)] rounded-full blur-[100px] pointer-events-none animate-shape-pulse"
          />

        <div className="container relative z-10">
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate={whyInView ? "show" : "hidden"}
          >
            <motion.div className="text-center mb-[50px] lg:mb-[70px]" variants={itemVariants}>
              <h2 className="text-[28px] md:text-[40px] lg:text-[48px] font-semibold leading-[1.1] mb-[16px]">
                {t("why.sectionTitle")}
              </h2>
              <p className="text-[15px] lg:text-[16px] font-light leading-[1.7] text-foreground/90 max-w-[500px] mx-auto">
                {t("why.sectionDescription")}
              </p>
            </motion.div>

            <motion.div variants={itemVariants}>
              <AnimatedCard
                className="rounded-[22px] lg:rounded-[28px]"
                circleClassName="w-[200px] h-[200px] blur-[100px]"
              >
                <BorderWrapper
                  wrapperClass="rounded-[22px] lg:rounded-[28px]"
                  className="rounded-[22px] lg:rounded-[28px] bg-gradient-to-b from-[#121124] to-[#0e0821] relative overflow-hidden"
                >
                  <div
                    className="absolute top-[-10%] left-[50%] -translate-x-1/2 w-[600px] h-[300px] bg-theme/[0.06] rounded-full blur-[80px] pointer-events-none animate-shape-pulse"
                    />

                  <div className="grid sm:grid-cols-2 lg:grid-cols-4 divide-y sm:divide-y-0 sm:divide-x divide-white/[0.06]">
                    {whyItems.map((item, i) => (
                      <div key={i} className="p-[28px] lg:p-[36px] relative group">
                        {/* Hover glow */}
                        <div className="absolute inset-0 bg-theme/[0.03] opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none" />
                        <div className="relative z-10">
                          <div className="text-[38px] lg:text-[48px] font-semibold leading-[1] text-theme/15 mb-[16px] select-none">
                            0{i + 1}
                          </div>
                          <h3 className="text-[15px] lg:text-[16px] font-semibold leading-[1.3] mb-[10px]">
                            {item.title}
                          </h3>
                          <p className="text-[15px] lg:text-[16px] font-light leading-[1.7] text-foreground/90">
                            {item.description}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </BorderWrapper>
              </AnimatedCard>
            </motion.div>
          </motion.div>
        </div>
      </section>

      <PageCta
        title={t("why.ctaTitle")}
        description={t("why.ctaDescription")}
        primaryLabel={t("why.ctaButton")}
      />
    </>
  );
};

export default IntegrationsWhy;
